import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { usePeriod } from '@/app/PeriodContext';
import { PeriodBadge } from '@/app/PeriodBadge';
import { formatMoney, formatDate, maskMoneyInput, parseInputToApi, moneyToInput } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';

type Purpose = {
  value: string;
  label: string;
  eliminates_on_consolidation: boolean;
  creates_intercompany_balance: boolean;
  requires_distinct_entities: boolean;
};

type Transfer = {
  id: number;
  transfer_date: string;
  amount: MoneyString;
  purpose: string;
  purpose_label: string;
  description: string | null;
  from_entity_id: number;
  from_entity_name: string | null;
  to_entity_id: number;
  to_entity_name: string | null;
  eliminate_on_consolidation: boolean;
  is_intercompany: boolean;
  is_card_payment: boolean;
  /** CA-01: some do total quando as duas pontas estão no escopo. */
  eliminated_in_current_scope: boolean;
};

export function TransfersPage({ selectedIds }: { selectedIds: number[] }) {
  const { period } = usePeriod();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Transfer | null>(null);
  const queryClient = useQueryClient();

  /**
   * Excluir devolve o dinheiro às duas contas de uma vez.
   *
   * A exclusão é lógica, e as consultas de saldo filtram por ela no JOIN — não
   * há perna a apagar nem saldo a recalcular. Por isso o texto do confirmar
   * fala das DUAS contas: é o que o usuário precisa conferir depois.
   */
  async function confirmRemove(t: Transfer) {
    const label = `${t.purpose_label} · ${formatMoney(t.amount)} · ${t.from_entity_name} → ${t.to_entity_name}`;

    if (!confirm(`Excluir a transferência?

${label}

O valor volta ao saldo das duas contas.`)) {
      return;
    }

    try {
      await api.delete(`/transfers/${t.id}`, { entityIds: selectedIds });

      void queryClient.invalidateQueries({ queryKey: ['transfers'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    } catch (error) {
      // A recusa vem com o caminho certo — "estorne pela tela de Cartões" —
      // e por isso é mostrada como está, sem virar uma mensagem genérica.
      alert(error instanceof ApiError ? error.message : 'Não foi possível excluir.');
    }
  }

  const list = useQuery({
    queryKey: ['transfers', period.from, period.to, selectedIds],
    queryFn: () =>
      api.get<{ data: Transfer[]; meta: { total: number } }>(
        `/transfers?from=${period.from}&to=${period.to}`,
        { entityIds: selectedIds },
      ),
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const purposes = useQuery({
    queryKey: ['transfer-purposes'],
    queryFn: () => api.get<{ data: Purpose[] }>('/transfer-purposes'),
    select: (r) => r.data,
    staleTime: Infinity, // enum do backend, não muda em runtime
  });

  const eliminatedTotal = (list.data?.data ?? [])
    .filter((t) => t.eliminated_in_current_scope)
    .reduce((sum, t) => sum + Number(t.amount), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Transferências</h1>
        <button
          onClick={() => setFormOpen(true)}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          + Nova transferência
        </button>
      </div>

      {/* A explicação que evita a dúvida mais comum do sistema */}
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Transferência move dinheiro entre suas contas ou entre suas entidades. Não é receita nem
        despesa — não entra no resultado nem consome orçamento.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-2.5">
        <PeriodBadge />
        <span className="ml-auto pb-2 text-sm text-slate-500 dark:text-slate-400">
          {list.data?.meta.total ?? 0} no período
        </span>
      </div>

      {/* CA-01 — a interface diz quanto sumiu do consolidado, em vez de
          deixar o usuário achar que o número não fecha. */}
      {eliminatedTotal > 0 && (
        <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <p className="font-medium text-indigo-900 dark:text-indigo-200">
            {formatMoney(eliminatedTotal.toFixed(4))} eliminados da visão consolidada
          </p>
          <p className="mt-0.5 text-indigo-800/80 dark:text-indigo-300/80">
            Operações entre suas próprias entidades. Cada uma aparece na visão individual, mas o
            grupo não ganhou nem perdeu dinheiro — contá-las inflaria o faturamento.
          </p>
        </div>
      )}

      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {list.isPending ? (
          <div className="animate-pulse p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="mb-2 h-9 rounded bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : list.isError ? (
          <p className="p-8 text-center text-sm text-rose-600">Erro ao carregar.</p>
        ) : list.data.data.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium">Nenhuma transferência no período</p>
            <button
              onClick={() => setFormOpen(true)}
              className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              + Nova transferência
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2.5 font-medium">Data</th>
                <th className="px-3 py-2.5 font-medium">Finalidade</th>
                <th className="px-3 py-2.5 font-medium">De → Para</th>
                <th className="px-3 py-2.5 text-right font-medium">Valor</th>
                <th className="px-3 py-2.5 font-medium">Consolidado</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.data.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="whitespace-nowrap px-3 py-2.5">{formatDate(t.transfer_date)}</td>
                  <td className="px-3 py-2.5">
                    <span className="font-medium">{t.purpose_label}</span>
                    {t.description && t.description !== t.purpose_label && (
                      <span className="ml-1.5 text-xs text-slate-400">{t.description}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">
                    {t.from_entity_name} <span className="text-slate-400">→</span> {t.to_entity_name}
                  </td>
                  {/* Cor neutra: transferência não é receita nem despesa (RN-10) */}
                  <td className="whitespace-nowrap px-3 py-2.5 text-right font-medium text-indigo-700 dark:text-indigo-400">
                    {formatMoney(t.amount)}
                  </td>
                  <td className="px-3 py-2.5">
                    {t.eliminated_in_current_scope ? (
                      <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                        eliminada
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">visível</span>
                    )}
                    {t.is_intercompany && (
                      <span className="ml-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                        gera saldo entre entidades
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    {/*
                      EDITAR vem antes de EXCLUIR, e não é ordem por acaso.

                      Corrigir uma data ou uma descrição é o caso comum;
                      apagar é o excepcional. Deixar o destrutivo primeiro
                      convida ao clique errado numa linha que mexe no saldo de
                      duas contas.
                    */}
                    <button
                      onClick={() => setEditing(t)}
                      className="mr-1 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-indigo-600"
                      title="Editar transferência"
                    >
                      editar
                    </button>
                    <button
                      onClick={() => confirmRemove(t)}
                      className="rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-rose-600"
                      title="Excluir transferência"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && reference.data && purposes.data && (
        <TransferForm
          reference={reference.data}
          purposes={purposes.data}
          selectedIds={selectedIds}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['transfers'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
            void queryClient.invalidateQueries({ queryKey: ['statement'] });
          }}
        />
      )}

      {editing && (
        <EditTransferDialog
          transfer={editing}
          selectedIds={selectedIds}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['transfers'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
            void queryClient.invalidateQueries({ queryKey: ['statement'] });
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function TransferForm({
  reference,
  purposes,
  selectedIds,
  onClose,
  onSaved,
}: {
  reference: ReferenceData;
  purposes: Purpose[];
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  const [purposeValue, setPurposeValue] = useState('transferencia_simples');
  const [fromEntityId, setFromEntityId] = useState(reference.entities[0]?.id ?? 0);
  const [toEntityId, setToEntityId] = useState(reference.entities[0]?.id ?? 0);
  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [amountMasked, setAmountMasked] = useState('');
  const [transferDate, setTransferDate] = useState(today);
  const [description, setDescription] = useState('');

  const purpose = purposes.find((p) => p.value === purposeValue);
  const amount = parseInputToApi(amountMasked);

  const fromAccounts = reference.accounts.filter((a) => a.entity_id === fromEntityId);
  const toAccounts = reference.accounts.filter((a) => a.entity_id === toEntityId);

  const sameEntity = fromEntityId === toEntityId;
  const violatesDistinct = (purpose?.requires_distinct_entities ?? false) && sameEntity;

  const save = useMutation({
    mutationFn: () =>
      api.post('/transfers', {
        from_entity_id: fromEntityId,
        to_entity_id: toEntityId,
        from_account_id: Number(fromAccountId),
        to_account_id: Number(toAccountId),
        amount,
        transfer_date: transferDate,
        purpose: purposeValue,
        ...(description ? { description } : {}),
      }, { entityIds: selectedIds }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  const canSubmit =
    Number(amount) > 0 &&
    fromAccountId !== '' &&
    toAccountId !== '' &&
    fromAccountId !== toAccountId &&
    !violatesDistinct;

  return (
    <Modal title="Nova transferência" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([f, m]) => <li key={f}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Finalidade" required>
          <select
            value={purposeValue}
            onChange={(e) => setPurposeValue(e.target.value)}
            className="input w-full"
          >
            {purposes
              .filter((p) => p.value !== 'pagamento_fatura') // fatura se paga na tela do cartão
              .map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
          </select>
        </Field>

        {/* A finalidade muda o comportamento no consolidado — o usuário
            precisa ver isso ANTES de salvar, não descobrir no relatório. */}
        {purpose && (
          <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
            {purpose.eliminates_on_consolidation ? (
              <p>
                Aparecerá na visão de cada entidade e será <strong>eliminada no consolidado</strong>,
                para não contar o mesmo dinheiro duas vezes.
              </p>
            ) : (
              <p>Movimento interno. Não afeta o resultado em nenhuma visão.</p>
            )}
            {purpose.creates_intercompany_balance && (
              <p className="mt-1">
                Gera saldo entre entidades: registra quem deve a quem até a quitação.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="De — entidade" required>
            <select
              value={fromEntityId}
              onChange={(e) => { setFromEntityId(Number(e.target.value)); setFromAccountId(''); }}
              className="input w-full"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
          <Field label="Para — entidade" required>
            <select
              value={toEntityId}
              onChange={(e) => { setToEntityId(Number(e.target.value)); setToAccountId(''); }}
              className="input w-full"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
        </div>

        {violatesDistinct && (
          <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            A finalidade “{purpose?.label}” exige entidades diferentes na origem e no destino.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Conta de origem" required>
            <select
              value={fromAccountId}
              onChange={(e) => setFromAccountId(e.target.value)}
              required
              className="input w-full"
            >
              <option value="">Selecione</option>
              {fromAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Conta de destino" required>
            <select
              value={toAccountId}
              onChange={(e) => setToAccountId(e.target.value)}
              required
              className="input w-full"
            >
              <option value="">Selecione</option>
              {toAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
        </div>

        {fromAccountId !== '' && fromAccountId === toAccountId && (
          <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            Origem e destino não podem ser a mesma conta — não haveria movimento nenhum.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor" required>
            <input
              value={amountMasked}
              onChange={(e) => setAmountMasked(maskMoneyInput(e.target.value))}
              required
              inputMode="numeric"
              placeholder="0,00"
              className="input w-full text-right"
            />
          </Field>
          <Field label="Data" required>
            <input
              type="date"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
        </div>

        <Field label="Descrição">
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            placeholder={purpose?.label}
            className="input w-full"
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Transferindo…' : 'Transferir'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------------

/**
 * Editar uma transferência.
 *
 * ================================================================
 * O QUE APARECE DEPENDE DO PROPÓSITO
 * ================================================================
 *
 * Transferência simples é um fato solto: data, valor, descrição — tudo pode
 * ser corrigido.
 *
 * As outras são o lado "caixa" de um documento que vive em outro lugar: a
 * liberação de uma dívida, a quitação de uma parcela, o pagamento de uma
 * fatura. Ali o VALOR vem da origem, e deixar mudar aqui criaria uma
 * divergência que o sistema não tem como explicar — a dívida dizendo que
 * liberou um número e a conta dizendo que recebeu outro.
 *
 * Então o campo de valor some, e o motivo aparece escrito. Um campo desabilitado
 * sem explicação faria a pessoa procurar defeito onde há regra.
 */
function EditTransferDialog({
  transfer,
  selectedIds,
  onClose,
  onSaved,
}: {
  transfer: Transfer;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const simples = transfer.purpose === 'transferencia_simples';

  const [date, setDate] = useState(transfer.transfer_date.slice(0, 10));
  const [description, setDescription] = useState(transfer.description ?? '');
  const [amountMasked, setAmountMasked] = useState(moneyToInput(transfer.amount));

  const save = useMutation({
    mutationFn: () =>
      /*
        `patch(caminho, CORPO, opcoes)` — três posições, nesta ordem.

        A primeira versão passava `{ entityIds, body }` como corpo, e o
        typecheck não pegou porque `body` é `unknown`. O servidor receberia um
        objeto sem nenhum dos campos e o cabeçalho de entidade sumiria — falha
        silenciosa em filtro de escopo, que é o pior tipo.
      */
      api.patch(
        `/transfers/${transfer.id}`,
        {
          transfer_date: date,
          description: description || null,
          ...(simples ? { amount: parseInputToApi(amountMasked) } : {}),
        },
        { entityIds: selectedIds },
      ),
    onSuccess: onSaved,
    onError: (e) =>
      alert(e instanceof ApiError ? e.message : 'Não foi possível salvar.'),
  });

  return (
    <Modal title="Editar transferência" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {transfer.purpose_label}
          {transfer.description ? ` · ${transfer.description}` : ''}
        </p>

        <Field label="Data" required>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input w-full"
          />
        </Field>

        {simples ? (
          <Field label="Valor" required>
            <input
              value={amountMasked}
              onChange={(e) => setAmountMasked(maskMoneyInput(e.target.value))}
              inputMode="numeric"
              className="input w-full text-right"
            />
          </Field>
        ) : (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <strong>{formatMoney(transfer.amount)}</strong> — o valor vem do documento que originou
            esta transferência e não muda por aqui. Se ela não deveria existir, exclua: a dívida e
            as parcelas continuam inteiras, e o saldo da conta volta ao que era.
          </div>
        )}

        <Field label="Descrição">
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input w-full"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm transition hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
